import React, { useEffect, useState } from "react";
import { MailX, Plus, X, ChevronDown, ChevronUp } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

function SkippedRow({ r, onDone }) {
  const e = r.email || {};
  const [type, setType] = useState(e.suggested_type || "brokerage");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const act = async (path, body, msg) => {
    setBusy(true);
    try { const { data } = await api.post(`/inbox/skipped/${r.id}/${path}`, body); toast.success(msg(data)); onDone(); }
    catch (err) { toast.error(formatError(err)); } finally { setBusy(false); }
  };
  return (
    <div data-testid={`skipped-row-${r.id}`} className="border border-slate-800 rounded-md p-3 bg-slate-950/50">
      <div className="flex flex-col md:flex-row md:items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-sm text-slate-100 break-words">{e.subject}</div>
          <div className="text-xs text-slate-500 mt-0.5 break-all">
            {e.sender_name} &lt;{e.sender_email}&gt; · {e.sent_at ? new Date(e.sent_at).toLocaleString() : "—"}
            {e.mawb_hint ? ` · MAWB ${e.mawb_hint}` : ""}
          </div>
          <div data-testid={`skipped-reason-${r.id}`} className="inline-block mt-1.5 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded bg-amber-500/10 text-amber-300 border border-amber-500/30">
            Skipped: {r.reason}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Select value={type} onValueChange={setType}>
            <SelectTrigger data-testid={`skipped-type-${r.id}`} className="w-32 h-8 bg-slate-950 border-slate-700"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
              <SelectItem value="brokerage">Brokerage</SelectItem>
              <SelectItem value="forwarding">Forwarding</SelectItem>
            </SelectContent>
          </Select>
          <Button data-testid={`skipped-import-${r.id}`} size="sm" disabled={busy} className="h-8 bg-blue-600 hover:bg-blue-500"
                  onClick={() => act("import", { shipment_type: type }, (d) => `Created ${d.shipment_number}`)}>
            <Plus size={14} className="mr-1" /> Make shipment
          </Button>
          <Button data-testid={`skipped-dismiss-${r.id}`} size="sm" variant="ghost" disabled={busy} title="Dismiss"
                  className="h-8 px-2 text-slate-400 hover:bg-slate-800" onClick={() => act("dismiss", {}, () => "Dismissed")}>
            <X size={14} />
          </Button>
        </div>
      </div>
      {e.body && (
        <button data-testid={`skipped-toggle-${r.id}`} onClick={() => setOpen(!open)} className="mt-2 text-xs text-sky-400 inline-flex items-center gap-1">
          {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />} {open ? "Hide" : "Show"} email
        </button>
      )}
      {open && <div className="mt-2 text-xs text-slate-300 whitespace-pre-wrap bg-slate-900/60 border border-slate-800 rounded p-2 max-h-48 overflow-auto">{e.body}</div>}
    </div>
  );
}

export const SkippedEmails = ({ onImported }) => {
  const [rows, setRows] = useState([]);
  const load = async () => {
    try { setRows((await api.get("/inbox/skipped")).data); } catch (e) { toast.error(formatError(e)); }
  };
  useEffect(() => { load(); }, []);
  return (
    <Dialog onOpenChange={(o) => { if (o) load(); }}>
      <DialogTrigger asChild>
        <Button data-testid="button-skipped-emails" variant="outline" className="border-slate-700 hover:bg-slate-800 text-slate-100">
          <MailX size={14} className="mr-2" /> Skipped emails ({rows.length})
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Skipped emails</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-slate-400 -mt-2">Emails the inbox did not turn into shipments. If one is a real pre-alert, pick the folder and click Make shipment.</p>
        <div data-testid="skipped-list" className="space-y-2">
          {rows.length === 0 && <div className="text-sm text-slate-500 py-6 text-center">No skipped emails.</div>}
          {rows.map((r) => <SkippedRow key={r.id} r={r} onDone={() => { load(); onImported(); }} />)}
        </div>
      </DialogContent>
    </Dialog>
  );
};
